import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import { Button, Tooltip } from '@mui/material';


import {  useDispatch } from 'react-redux';

import { NavLink } from 'react-router-dom';


import { showSuccessSnackbar, showErrorSnackbar } from 'store/actions/snackbarMessage'
import { publishMenu, publishSecondaryMenu, unpublishQRMenu } from 'store/actions/restaurantMenu';
import { useConfirm } from 'material-ui-confirm';
import API from 'util/API';
import { createMenuPreview } from 'apis/menuAPIs';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: theme.spacing(2)
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center',
    }
  },
  spacer: {
    flexGrow: 1,
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  buttonGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    justifyContent: 'center',
    alignItems: 'center'
  },
  button: {
    minWidth: '140px',
    height: '42px',
    padding: theme.spacing(1, 2),
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: theme.spacing(1),
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '120px',
      fontSize: '0.8rem',
      padding: theme.spacing(0.75, 1.5),
    }
  },
  primaryButton: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText} !important`,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.dark} !important`,
    }
  },
  previewButton: {
    backgroundColor: '#0288d1 !important',
    color: `${theme.palette.common.white} !important`,
    '&:hover': {
      backgroundColor: '#01579b !important'
    }
  },
  publishButton: {
    backgroundColor: '#4caf50 !important',
    color: `${theme.palette.common.white} !important`,
    '&:hover': {
      backgroundColor: '#388e3c !important'
    }
  },
  unpublishButton: {
    backgroundColor: '#f44336 !important',
    color: `${theme.palette.common.white} !important`,
    '&:hover': {
      backgroundColor: '#d32f2f !important'
    }
  },
  serviceButton: {
    backgroundColor: '#ff9800 !important',
    color: `${theme.palette.common.white} !important`,
    '&:hover': {
      backgroundColor: '#f57c00 !important'
    }
  },
  categoryButton: {
    backgroundColor: '#9c27b0 !important',
    color: `${theme.palette.common.white} !important`,
    '&:hover': {
      backgroundColor: '#7b1fa2 !important'
    }
  },
  cookingButton: {
    backgroundColor: '#607d8b !important',
    color: `${theme.palette.common.white} !important`,
    '&:hover': {
      backgroundColor: '#455a64 !important'
    }
  },
  buttonIcon: {
    fontSize: '18px',
    marginRight: theme.spacing(0.5)
  }
}));

const MenuToolbar = props => {
  const { className, menu, menuId, ...rest } = props;
  
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const showSuccessMessage = (msg) => dispatch(showSuccessSnackbar(msg));
  const showError = (msg) => dispatch(showErrorSnackbar(msg));
  const publishTheMenu = (id, qrCodeType) => dispatch(publishMenu(id, qrCodeType));
  const unpublishTheQRMenu = (id, qrCodeType) => dispatch(unpublishQRMenu(id, qrCodeType));
  
  const publishTheSecondaryMenu = (id) => dispatch(publishSecondaryMenu(id));

  const classes = useStyles();

  const handlePublish = () => {
    if (menu.isMainMenu === false) {
      publishTheSecondaryMenu(menuId);
    } else {
      confirm({ 
        title: 'Publish Menu',
        description: 'You are about to publish this menu to the website. Are you sure?',
        confirmationText: 'Publish',
        cancellationText: 'Cancel'
      })
        .then(() => {
          publishTheMenu(menuId);
        })
        .catch(() => { /* ... */ });
    }
  };

  const handlePublishOnline = () => {
    if (menu.qrCodeType.length === 0) {
      showError('Please select a QR code type for this menu.')
      return;
    } else {
      confirm({ 
        title: 'Publish QR Menu',
        description: 'You are about to publish this menu online with QR code access. Continue?',
        confirmationText: 'Publish',
        cancellationText: 'Cancel'
      })
        .then(() => {
          publishTheMenu(menuId, menu.qrCodeType);
        })
        .catch(() => { /* ... */ });
    }
  };

  const handleUnpublish = () => {
    if (menu.qrCodeType.length === 0) {
      showError('Please select a QR code type for this menu.')
      return;
    } else {
      confirm({ 
        title: 'Unpublish QR Menu',
        description: 'You are about to unpublish this menu from online QR code access. Continue?',
        confirmationText: 'Unpublish',
        cancellationText: 'Cancel'
      })
        .then(() => {
          unpublishTheQRMenu(menuId, menu.qrCodeType);
        })
        .catch(() => { /* ... */ });
    }
  };

  // Preview never touches the live menu - it resolves the draft the way
  // publishing would and puts it behind an expiring short link, so the menu can
  // be checked on a phone before anyone commits to publishing it.
  const handlePreview = async () => {
    try {
      const result = await createMenuPreview(menuId);
      const link = result.shortUrl || result.url;
      const warningCount = (result.warnings || []).length;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link).catch(() => { /* clipboard blocked */ });
      }

      confirm({
        title: 'Preview link ready',
        description: warningCount > 0
          ? `${link}\n\nCopied to clipboard. ${warningCount} thing${warningCount === 1 ? '' : 's'} to check before publishing - listed on the preview page.`
          : `${link}\n\nCopied to clipboard. Nothing flagged.`,
        confirmationText: 'Open preview',
        cancellationText: 'Done',
      })
        .then(() => window.open(link, '_blank', 'noopener'))
        .catch(() => { /* dismissed */ });
    } catch (err) {
      showError('Could not create the preview link, please try again.');
    }
  };

  const handleOpitonLinksRefresh = async () => {
    try {
      await API.post('/refreshOptionLinks/' + menuId);
      showSuccessMessage('Option links refreshed successfully');
    } catch (err) {
      showError('Something went wrong, please try again.');
    }
  };



  return (
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >
      <div className={classes.row}>
        <span className={classes.spacer} />
        <div className={classes.buttonGroup}>
          <Tooltip title="Refresh option links for menu items">
            <Button 
              variant="contained" 
              className={clsx(classes.button, classes.primaryButton)}
              onClick={handleOpitonLinksRefresh}
              startIcon={<i className={clsx("material-icons", classes.buttonIcon)}>refresh</i>}
            >
              Refresh Options
            </Button>
          </Tooltip>
          
          <Tooltip title="Get a shareable link to preview this menu without publishing">
            <Button
              variant="contained"
              className={clsx(classes.button, classes.previewButton)}
              onClick={handlePreview}
              startIcon={<i className={clsx("material-icons", classes.buttonIcon)}>visibility</i>}
            >
              Preview
            </Button>
          </Tooltip>

          <Tooltip title="Publish main menu">
            <Button 
              variant="contained" 
              className={clsx(classes.button, classes.publishButton)}
              onClick={handlePublish}
              startIcon={<i className={clsx("material-icons", classes.buttonIcon)}>publish</i>}
            >
              Publish Main
            </Button>
          </Tooltip>
          
          <Tooltip title="Publish menu with QR code access">
            <Button 
              variant="contained" 
              className={clsx(classes.button, classes.publishButton)}
              onClick={handlePublishOnline}
              startIcon={<i className={clsx("material-icons", classes.buttonIcon)}>qr_code</i>}
            >
              Publish QR
            </Button>
          </Tooltip>
          
          <Tooltip title="Unpublish QR code menu">
            <Button 
              variant="contained" 
              className={clsx(classes.button, classes.unpublishButton)}
              onClick={handleUnpublish}
              startIcon={<i className={clsx("material-icons", classes.buttonIcon)}>unpublished</i>}
            >
              Unpublish QR
            </Button>
          </Tooltip>

          <Tooltip title="Edit service availability hours">
            <NavLink 
              to={{
                pathname: `/menu/${props.menuId}/service-availability`,
                state: { menu: menu }
              }} 
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant="contained"
                className={clsx(classes.button, classes.serviceButton)}
                startIcon={<i className={clsx("material-icons", classes.buttonIcon)}>UberEats</i>}
              >
                Service Hours
              </Button>
            </NavLink>
          </Tooltip>

          <Tooltip title="Manage cooking categories">
            <NavLink
              to={{
                pathname: '/menuCookingCategoryList',
                state: { menuId: props.menuId }
              }}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant="contained"
                className={clsx(classes.button, classes.cookingButton)}
                startIcon={<i className={clsx("material-icons", classes.buttonIcon)}>restaurant</i>}
              >
                Cooking Categories
              </Button>
            </NavLink>
          </Tooltip>

          <Tooltip title="Manage menu categories">
            <NavLink to={'/menuCategory/' + props.menuId} style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                className={clsx(classes.button, classes.categoryButton)}
                startIcon={<i className={clsx("material-icons", classes.buttonIcon)}>category</i>}
              >
                Menu Categories
              </Button>
            </NavLink>
          </Tooltip>

          <Tooltip title="Edit categories, dishes and choices in the new menu editor">
            <NavLink to={'/menuEditor/' + props.menuId} style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                className={clsx(classes.button, classes.categoryButton)}
                startIcon={<i className={clsx("material-icons", classes.buttonIcon)}>edit_note</i>}
              >
                Menu Editor (new)
              </Button>
            </NavLink>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

MenuToolbar.propTypes = {
  className: PropTypes.string
};

export default MenuToolbar;
